import Decimal from 'decimal.js';

import { calculatedLineAmount, compareMoney, parseDecimalOrNull, sumLineAmounts } from './arithmetic';

/**
 * "Faltan 20,00, y vienen de la fila 2" - the redesign's requirement that a total mismatch
 * names the amount and, where possible, the one row that accounts for it (CLAUDE.md: "point at
 * the likely bad field, not just 'el total no cuadra'"). Falls back to a generic phrase when the
 * gap cannot be attributed to a single row - `review/arithmetic.ts`'s existing reconciliation
 * stays the source of truth for *whether* something disagrees; this only explains *what*.
 */
export function totalMismatchPhrase(
  writtenTotal: string | null,
  lines: readonly { readonly quantity: string; readonly unitPrice: string; readonly lineAmount: string }[],
): string | null {
  const written = parseDecimalOrNull(writtenTotal);
  if (written === null) {
    return null;
  }
  const amounts = lines.map((line) => parseDecimalOrNull(line.lineAmount === '' ? null : line.lineAmount));
  if (amounts.some((amount) => amount === null)) {
    return 'El total no cuadra. Falta un importe en alguna fila para poder comprobarlo.';
  }
  const sum = amounts.reduce<Decimal>((total, amount) => total.plus(amount ?? new Decimal(0)), new Decimal(0));
  const delta = written.minus(sum);
  if (delta.isZero()) {
    return null;
  }
  const absoluteDelta = delta.abs().toFixed(2);
  const matchingLine = lines.findIndex((line) => {
    const calculated = calculatedLineAmount(line.quantity === '' ? null : line.quantity, line.unitPrice === '' ? null : line.unitPrice);
    const amount = parseDecimalOrNull(line.lineAmount === '' ? null : line.lineAmount);
    if (calculated === null || amount === null) {
      return false;
    }
    return calculated.minus(amount).abs().toFixed(2) === absoluteDelta;
  });
  if (matchingLine !== -1) {
    return `Faltan ${absoluteDelta}, y vienen de la fila ${String(matchingLine + 1)}.`;
  }
  return delta.isPositive()
    ? `Faltan ${absoluteDelta} para llegar al total escrito.`
    : `Sobran ${absoluteDelta} sobre el total escrito.`;
}

/** Convenience wrapper matching `arithmetic.ts`'s `compareMoney` gate: only computes a phrase
 * when the totals actually disagree, so a caller does not have to re-check agreement itself. */
export function totalMismatchPhraseIfDisagreeing(
  writtenTotal: string,
  lines: readonly { readonly quantity: string; readonly unitPrice: string; readonly lineAmount: string }[],
): string | null {
  const sum = sumLineAmounts(lines.map((line) => (line.lineAmount === '' ? null : line.lineAmount)));
  const agreement = compareMoney(writtenTotal === '' ? null : writtenTotal, sum);
  if (agreement !== 'disagrees') {
    return null;
  }
  return totalMismatchPhrase(writtenTotal === '' ? null : writtenTotal, lines);
}
